import { useState } from 'react';
import { cn } from '../../lib/cn';
import { formatNumber, formatPercent } from '../../lib/format';

/**
 * CURVA DE SOBREVIVÊNCIA — "% que continua no grupo", em degraus, de 100% para baixo.
 *
 * Organismo de dado (Curva de Evasão, ADR-0019). Recebe frações PRONTAS por marco (D0…D90) —
 * nada aqui calcula métrica (invariante 3). O eixo X é por MARCO, igualmente espaçado: em
 * escala de tempo linear o D90 esmagaria D0…D7, que é justamente onde o criativo ruim aparece.
 *
 * - `null` num marco = **ainda não deu tempo**: a curva PARA ali (não cai a zero);
 * - de 1 a 3 séries, cor fixa `chart-1…3`; `dashed` marca a série "sem atribuição";
 * - a legenda é clicável (esconde/mostra a série) e traz o `n` de cada uma.
 */
export interface SurvivalSeries {
  id: string;
  label: string;
  /** Quantas pessoas formam a série (vai na legenda). */
  n: number;
  /** Fração que ficou, uma por marco, na ordem de `milestones`. */
  values: readonly (number | null)[];
  dashed?: boolean;
}

export interface SurvivalChartProps {
  milestones: readonly number[];
  series: readonly SurvivalSeries[];
  /** Descrição curta para leitor de tela. */
  ariaLabel: string;
  yLabel?: string;
  className?: string;
}

const CORES = ['var(--color-chart-1)', 'var(--color-chart-2)', 'var(--color-chart-3)'] as const;

/** Coordenadas internas do SVG (o desenho escala no contêiner). */
const W = 640;
const H = 220;
const PAD = { top: 12, right: 16, bottom: 26, left: 40 };

export function SurvivalChart({
  milestones,
  series,
  ariaLabel,
  yLabel = '% que continua no grupo',
  className,
}: SurvivalChartProps) {
  const [ocultas, setOcultas] = useState<ReadonlySet<string>>(new Set());
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  // Posição 0 = a entrada (100%); depois um passo por marco.
  const passos = milestones.length;
  const x = (i: number) => PAD.left + (plotW * i) / Math.max(1, passos);
  const y = (v: number) => PAD.top + plotH - plotH * Math.max(0, Math.min(1, v));

  const caminho = (values: readonly (number | null)[]) => {
    let d = `M ${x(0)} ${y(1)}`;
    for (let i = 0; i < values.length; i += 1) {
      const v = values[i];
      // Marco não vencido: a curva para aqui, não cai a zero.
      if (v === null || v === undefined) break;
      d += ` H ${x(i + 1)} V ${y(v)}`;
    }
    return d;
  };

  const alternar = (id: string) =>
    setOcultas((atual) => {
      const nova = new Set(atual);
      if (nova.has(id)) nova.delete(id);
      else nova.add(id);
      return nova;
    });

  return (
    <figure className={cn('flex flex-col gap-3', className)}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={ariaLabel}
        className="block h-auto w-full"
      >
        {[0, 0.25, 0.5, 0.75, 1].map((t) => (
          <g key={t}>
            <line
              x1={PAD.left}
              x2={W - PAD.right}
              y1={y(t)}
              y2={y(t)}
              stroke="var(--color-chart-grid)"
              strokeWidth="1"
            />
            <text
              x={PAD.left - 6}
              y={y(t) + 3}
              textAnchor="end"
              className="nums fill-muted-foreground text-2xs"
            >
              {formatPercent(t, 0)}
            </text>
          </g>
        ))}
        <text x={x(0)} y={H - 8} className="fill-muted-foreground text-2xs">
          entrada
        </text>
        {milestones.map((m, i) => (
          <text
            key={m}
            x={x(i + 1)}
            y={H - 8}
            textAnchor="middle"
            className="nums fill-muted-foreground text-2xs"
          >
            D{m}
          </text>
        ))}
        {series.map((s, idx) =>
          ocultas.has(s.id) ? null : (
            <path
              key={s.id}
              d={caminho(s.values)}
              fill="none"
              stroke={CORES[idx % CORES.length]}
              strokeWidth="2"
              strokeDasharray={s.dashed ? '6 4' : undefined}
              strokeLinejoin="round"
            />
          ),
        )}
      </svg>
      <figcaption className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
        <span className="sr-only">{yLabel}</span>
        {series.map((s, idx) => (
          <button
            key={s.id}
            type="button"
            aria-pressed={!ocultas.has(s.id)}
            onClick={() => alternar(s.id)}
            className={cn(
              'hit-44 inline-flex items-center gap-1.5 rounded-pill px-1 text-left',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              ocultas.has(s.id) && 'opacity-45',
            )}
          >
            <svg viewBox="0 0 16 4" aria-hidden="true" className="h-1 w-4">
              <line
                x1="0"
                x2="16"
                y1="2"
                y2="2"
                stroke={CORES[idx % CORES.length]}
                strokeWidth="3"
                strokeDasharray={s.dashed ? '4 2' : undefined}
              />
            </svg>
            <span className="text-foreground">{s.label}</span>
            <span className="nums">n={formatNumber(s.n)}</span>
          </button>
        ))}
      </figcaption>
    </figure>
  );
}
